import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/*
 * Props:
 *   value, idField, displayField, selectedField, codeField, hideCode, twoLine,
 *   onChange, onAdd, addLabel, placeholder, error, dropdownMinWidth, label.
 *
 * Modo local (default):
 *   - options: array completo de opciones; filtra en memoria.
 *
 * Modo remoto:
 *   - asyncSearch(query) => Promise<opciones[]>   (habilita modo remoto)
 *   - minChars: mínimo de caracteres antes de buscar (default 3)
 *   - initialSelected: objeto preseleccionado (necesario cuando el `value` llega antes
 *     de que `options` se haya cargado; p.ej. al editar un documento existente).
 */
const AutoComplete = ({
  label, value, options, idField, displayField, selectedField,
  hideCode, dropdownMinWidth, onChange, onAdd, addLabel, placeholder,
  twoLine, codeField = 'codigo', error,
  asyncSearch, minChars = 3, initialSelected,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const searchSeqRef = useRef(0); // para descartar respuestas obsoletas
  const [dropdownStyle, setDropdownStyle] = useState({ position: 'fixed', top: -9999, left: -9999, visibility: 'hidden' });

  const isRemote = typeof asyncSearch === 'function';
  const sourceOptions = isRemote ? remoteOptions : (options || []);

  const getDisplay = useCallback((o) => {
    if (!o) return '';
    if (typeof displayField === 'function') return displayField(o);
    return o[displayField] ?? '';
  }, [displayField]);

  // Resolución del objeto seleccionado: busca en options, en resultados remotos,
  // y cae a initialSelected si coincide el ID.
  const selected = (() => {
    if (!value && value !== 0) return null;
    const strVal = String(value);
    const fromLocal = (options || []).find(o => String(o[idField]) === strVal);
    if (fromLocal) return fromLocal;
    const fromRemote = remoteOptions.find(o => String(o[idField]) === strVal);
    if (fromRemote) return fromRemote;
    if (initialSelected && String(initialSelected[idField]) === strVal) return initialSelected;
    return null;
  })();

  useEffect(() => {
    const handler = (e) => {
      const inWrapper  = ref.current && ref.current.contains(e.target);
      const inDropdown = e.target.closest?.('.autocomplete-dropdown');
      if (!inWrapper && !inDropdown) {
        setOpen(false);
        setSearch('');   // limpiar texto si no se seleccionó nada
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const updatePosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 260 && rect.top > 260;
    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      top: openUpward ? 'auto' : rect.bottom + 4,
      bottom: openUpward ? (window.innerHeight - rect.top + 4) : 'auto',
      minWidth: Math.max(rect.width, dropdownMinWidth || 220),
      maxWidth: Math.min(640, window.innerWidth - rect.left - 12),
      width: 'max-content',
      zIndex: 100000,
      visibility: 'visible',
    });
  }, [dropdownMinWidth]);

  useEffect(() => {
    if (open) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
        setDropdownStyle({ position: 'fixed', top: -9999, left: -9999, visibility: 'hidden' });
      };
    }
  }, [open, updatePosition]);

  // Búsqueda remota con debounce 250ms
  useEffect(() => {
    if (!isRemote) return;
    if (!open || selected) return;
    if (search.trim().length < minChars) {
      setRemoteOptions([]);
      setLoading(false);
      return;
    }
    const seq = ++searchSeqRef.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await asyncSearch(search.trim());
        if (seq !== searchSeqRef.current) return; // descartado, llegó una búsqueda más reciente
        const list = Array.isArray(res) ? res : (res?.data || []);
        setRemoteOptions(list);
      } catch {
        if (seq === searchSeqRef.current) setRemoteOptions([]);
      } finally {
        if (seq === searchSeqRef.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, isRemote, minChars, asyncSearch, selected]);

  const filtered = isRemote
    ? sourceOptions
    : (sourceOptions
        .filter(o => getDisplay(o).toLowerCase().includes(search.toLowerCase()))
        .slice(0, 20));

  const handleSelect = (item) => {
    onChange(item[idField], item);
    setSearch('');
    setOpen(false);
    setRemoteOptions([]);
  };

  const handleClear = () => {
    onChange('', null);
    setSearch('');
    setRemoteOptions([]);
  };

  const renderDropdownContent = () => {
    if (isRemote) {
      if (search.trim().length < minChars) {
        return <div className="autocomplete-empty">Escribe al menos {minChars} caracteres...</div>;
      }
      if (loading) {
        return <div className="autocomplete-empty">Buscando...</div>;
      }
      if (filtered.length === 0) {
        return <div className="autocomplete-empty">Sin resultados</div>;
      }
    } else if (filtered.length === 0) {
      return <div className="autocomplete-empty">Sin resultados</div>;
    }
    return filtered.map(o => (
      <div key={o[idField]} className="autocomplete-option" onMouseDown={() => handleSelect(o)}>
        {!hideCode && o.codigo && typeof displayField !== 'function' && <span className="autocomplete-option-code">{o.codigo}</span>}
        <span>{getDisplay(o)}</span>
      </div>
    ));
  };

  const dropdown = open && !selected ? ReactDOM.createPortal(
    <div className="autocomplete-dropdown" style={dropdownStyle}>
      {renderDropdownContent()}
    </div>,
    document.body
  ) : null;

  return (
    <div className={`form-row${twoLine ? ' form-row--stretch' : ''}`} ref={ref}>
      {label && <label>{label}</label>}
      <div className="autocomplete-wrap" style={error ? { boxShadow: '0 0 0 1.5px #ef4444, 0 0 0 4px rgba(239,68,68,0.10)', borderRadius: 8 } : undefined}>
        <div className="autocomplete-input-row">
          {selected ? (
            twoLine ? (
              <div className="autocomplete-selected autocomplete-selected--two-line">
                <div className="autocomplete-selected-content">
                  {selected[codeField] && <span className="autocomplete-selected-code">{selected[codeField]}</span>}
                  <span className="autocomplete-selected-text">{selectedField ? selected[selectedField] : getDisplay(selected)}</span>
                </div>
                <button type="button" className="autocomplete-clear" onClick={handleClear}>&times;</button>
              </div>
            ) : (
              <div className="autocomplete-selected">
                <span className="autocomplete-selected-text">{selectedField ? selected[selectedField] : getDisplay(selected)}</span>
                <button type="button" className="autocomplete-clear" onClick={handleClear}>&times;</button>
              </div>
            )
          ) : (
            <input
              ref={inputRef}
              className="autocomplete-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder || (label ? `Buscar ${label.toLowerCase()}...` : 'Buscar...')}
              autoComplete="off"
            />
          )}
          {onAdd && (
            <button type="button" className="autocomplete-add-btn" onClick={onAdd} title={`Crear ${addLabel || label || ''}`}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
            </button>
          )}
        </div>
        {dropdown}
      </div>
    </div>
  );
};

export default AutoComplete;
